import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@fluentui/react-components';
import { IconContext } from 'react-icons';
import { GuideDrawer, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { QuickLinksSettings, WidgetProps } from '../Dashboard.Types';
import { quickLinkItems, selectedQuickLinks } from './Widgets.Functions';

export const targetsOf = (settings: WidgetProps['widget']['settings']): string[] | undefined => {
  const keys = (settings as Partial<QuickLinksSettings> | undefined)?.keys;
  return Array.isArray(keys) ? keys.filter((key): key is string => typeof key === 'string') : undefined;
};

/** Accesos directos a las secciones de la aplicación que el usuario elige (por defecto, las principales). */
export const QuickLinksWidget = ({ widget, settingsOpen, onCloseSettings, onSettingsChange }: WidgetProps) => {
  const { t } = useTranslation();
  const targets = React.useMemo(() => targetsOf(widget.settings), [widget.settings]);
  const links = React.useMemo(() => selectedQuickLinks(targets), [targets]);
  const all = React.useMemo(() => quickLinkItems(), []);
  const [draft, setDraft] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (settingsOpen) setDraft(links.map((item) => item.target));
  }, [settingsOpen, links]);

  const toggle = (target: string, checked: boolean) =>
    setDraft((current) => {
      const without = current.filter((candidate) => candidate !== target);
      if (!checked) return without;
      // Mantiene el orden del menú lateral.
      return all.map((item) => item.target).filter((candidate) => candidate === target || without.includes(candidate));
    });

  const save = () => {
    onSettingsChange({ keys: draft });
    onCloseSettings();
  };

  return (
    <>
      {links.length === 0 ? (
        <GuideEmptyState title={t('dashboard.widgets.quickLinks.empty')} icon={null} data-testid="quick-links-empty" />
      ) : (
        <IconContext.Provider value={{ size: '20px' }}>
          <ul className="dbw-links" data-testid="quick-links-list">
            {links.map((item) => (
              <li key={item.target}>
                <Link to={`/${item.target}`} className="dbw-links__item" data-testid={`quick-link-${item.target}`}>
                  <span className="dbw-links__icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="dbw-links__label">{t(item.name)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </IconContext.Provider>
      )}

      <GuideDrawer
        open={settingsOpen}
        onDismiss={onCloseSettings}
        size="small"
        title={t('dashboard.widgets.quickLinks.settingsTitle')}
        subtitle={t('dashboard.widgets.quickLinks.settingsText')}
        primaryAction={{ label: t('dashboard.common.save'), onClick: save, testId: 'quick-links-save' }}
        testId="quick-links-drawer"
      >
        <GuideDrawerSection>
          <div className="dbw-checklist">
            {all.map((item) => (
              <Checkbox
                key={item.target}
                label={t(item.name)}
                checked={draft.includes(item.target)}
                onChange={(_event, data) => toggle(item.target, !!data.checked)}
                data-testid={`quick-link-option-${item.target}`}
              />
            ))}
          </div>
        </GuideDrawerSection>
      </GuideDrawer>
    </>
  );
};

export default QuickLinksWidget;
